import { useEffect, useMemo, useState } from "react";
import Buttons from "../../../../components/ui/button/Buttons";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import SwalAlert from "../../../../services/swalService/SwalService";
import { decryptAESID } from "../../../../utils/Encryption";
import { useLazyGetProductDetailsListByProductInformationIdQuery, useUpdateApplicationStatusOnPeerReviewerReturnMutation, useLazyGetCertificateGroupingDetailsForWithRatingByIdQuery, useApproveSGBPSApplicationByFinalApproverMutation } from "../../../../app/services/CertificateGrouping/certificateGroupingApi";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../../../app/services/productAndService/productAndServiceApi";
import ModalGroupingTable from "./components/ModalGroupingTable";
import CostAllocation from "./components/CostAllocation";
import { useLazyGetPeerReviewEvaluationDetailsByIdQuery, useReturnApplicationToCaseOfficerByPeerReviewerMutation } from "../../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import { useAddAuditLogForSGBPApplcation } from "../../../../utils/AuditLogQuery/addAuditLog";
import ToastService from "../../../../services/toastService/ToastService";

export const DEFAULT_STATUS_ID = 15;

const TICK_OPTIONS = [
  { label: "1 Tick", value: 1 },
  { label: "2 Ticks", value: 2 },
  { label: "3 Ticks", value: 3 },
  { label: "4 Ticks", value: 4 },
];

const splitCert = (value = "") => {
  const i = value.lastIndexOf("/");
  return i === -1 ? [value, 0] : [value.slice(0, i), Number(value.slice(i + 1)) || 0];
};

const buildCostAllocationRows = (list, itemCodes, savedList = []) => {
  const labels = [
    ...new Set(
      (list || [])
        .filter((item) => item?.applicationNumber)
        .map((item) => (item.certPrefix ? `${item.applicationNumber}/${item.certPrefix}` : item.applicationNumber))
    ),
  ];

  return labels.map((label, index) => {
    const [num, prefix] = splitCert(label);
    const saved = savedList.find((x) => {
      const [xNum, xPrefix] = splitCert(x.certificateNumber);
      return xNum === num && xPrefix === prefix;
    });
    return {
      id: index + 1,
      label,
      options: itemCodes,
      value: itemCodes.find((i) => i.value === saved?.itemCode) ?? null,
      tickValue: TICK_OPTIONS.find((t) => t.value === saved?.tickRating) ?? null,
      tickOptions: TICK_OPTIONS,
    };
  });
};

const getApiErrorMessage = (err) => err?.data?.errorMessage || "Something went wrong. Please try again.";


const CertificateWithoutRatingFinalReview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editID = decryptAESID(id, false);
  const { confirm } = SwalAlert();
  const { addAuditLog } = useAddAuditLogForSGBPApplcation();

  const [internalNotes, setInternalNotes] = useState("");
  const [approver, setApprover] = useState();
  const [peerReviewer, setPeerReviewer] = useState();
  const [costAllocationList, setCostAllocationList] = useState([]);
  const [peerReviewerComment, setPeerReviewerComment] = useState("");
  const [approverReviewerComment, setApproverReviewerComment] = useState("");

  const [getProductDetailsListByProductInformationId, { isLoading: productListLoading, data: productListData }] = useLazyGetProductDetailsListByProductInformationIdQuery();
  const [getCertificateGroupingDetailsForWithRatingById, { isLoading: certificateGroupingDetailsLoading, data: certificateGroupingDetailsData }] = useLazyGetCertificateGroupingDetailsForWithRatingByIdQuery();
  const [productsAndServicesDropdown, { data: productsAndServicesDropDownsData, isLoading: productsAndServicesDropDownsLoading }] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [getPeerReviewEvaluationDetailsById, { isLoading: getPeerReviewEvaluationDetailsByIdLoading, isSuccess: getPeerReviewEvaluationDetailsByIdSuccess, data: getPeerReviewEvaluationDetailsByIdData, },] = useLazyGetPeerReviewEvaluationDetailsByIdQuery();

  const [returnToCaseOfficer, { isLoading: returnToCaseOfficeLoading }] = useReturnApplicationToCaseOfficerByPeerReviewerMutation();
  const [updateApplicationStatusOnPeerReviewerReturn, { isLoading: updateStatusOnReturnLoading }] = useUpdateApplicationStatusOnPeerReviewerReturnMutation();
  const [approveSGBPSApplicationByFinalApprover, { isLoading: approveApplicationLoading }] = useApproveSGBPSApplicationByFinalApproverMutation();

  const approverOptionsMemo = useMemo(() =>
    productsAndServicesDropDownsData?.filter((item) => item.dropdownName === "Approver List").map((item) => ({ value: item.id, label: item.name })) ?? [],
    [productsAndServicesDropDownsData]
  );

  const itemCodeOptionsMemo = useMemo(() =>
    productsAndServicesDropDownsData?.filter((item) => item.dropdownName === "Items Codes").map((item) => ({ value: item.id, label: item.name })) ?? [],
    [productsAndServicesDropDownsData]
  );

  const productList = useMemo(
    () => (Array.isArray(productListData) ? productListData : productListData?.data ?? []),
    [productListData]
  );

  useEffect(() => {
    const container = document.querySelector(".center-container");
    if (container) container.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (editID > 0) {
      productsAndServicesDropdown();
      getProductDetailsListByProductInformationId(editID);
      getCertificateGroupingDetailsForWithRatingById(editID);
      getPeerReviewEvaluationDetailsById(editID);
    }
  }, [editID]);


  useEffect(() => {
    if (!certificateGroupingDetailsData || !productListData || !productsAndServicesDropDownsData) return;
    const saved = certificateGroupingDetailsData.getCertificateAndCostAllocationForWithRatingList ?? [];
    setCostAllocationList(buildCostAllocationRows(productList, itemCodeOptionsMemo, saved));
    setPeerReviewer(approverOptionsMemo.find((o) => o.value === certificateGroupingDetailsData.peerReviewerId) ?? null);
    setApprover(approverOptionsMemo.find((o) => o.value === certificateGroupingDetailsData.approverId) ?? null);
    setInternalNotes(certificateGroupingDetailsData.commentFromCaseOfficer || "");
  }, [certificateGroupingDetailsData, productListData, productList, productsAndServicesDropDownsData, itemCodeOptionsMemo, approverOptionsMemo]);

  useEffect(() => {
    if (!getPeerReviewEvaluationDetailsByIdLoading && getPeerReviewEvaluationDetailsByIdSuccess && getPeerReviewEvaluationDetailsByIdData) {
      setPeerReviewerComment(getPeerReviewEvaluationDetailsByIdData?.peerReviewerComment ?? "");
      setApproverReviewerComment(getPeerReviewEvaluationDetailsByIdData?.finalApproverComment ?? "");
    }
  }, [getPeerReviewEvaluationDetailsByIdLoading, getPeerReviewEvaluationDetailsByIdSuccess, getPeerReviewEvaluationDetailsByIdData]);

  const handleBack = () => {
    navigate(`/FinalApproverForService/${id}`);
  };

  const handleReturn = () => {
    confirm("Return?", "Are you sure you want to return?", "Return", "Cancel").then((confirmed) => {
      if (confirmed) handleReturnApplication();
    });
  };

  const handleReturnApplication = async () => {
    try {
      const returnRes = await returnToCaseOfficer({
        sGBPProductInformationId: decryptAESID(id),
        peerReviewerComment,
        FinalApproverComment: approverReviewerComment,
      }).unwrap();
      if (returnRes?.hasError) {
        ToastService.error(returnRes.errorMessage);
        return;
      }

      const statusRes = await updateApplicationStatusOnPeerReviewerReturn({
        SGBPProductInformationId: decryptAESID(id),
      }).unwrap();
      if (statusRes?.hasError) {
        ToastService.error(statusRes.errorMessage);
        return;
      }

      ToastService.success("Application Returned to Case Officer");
      addAuditLog({
        SGBPSApplicationId: editID,
        AuditLogDescription: "SGBS Application Returned to Case Officer By Final Approver",
        TriggredTimeStatusId: certificateGroupingDetailsData?.statusId ?? DEFAULT_STATUS_ID,
      });
        navigate("/viewApplicationTaskList");
    } catch (err) {
      ToastService.error(getApiErrorMessage(err));
    }
  };

  const handleApprove = async () => {
    try {
      const res = await approveSGBPSApplicationByFinalApprover({
        sGBPProductInformationId: decryptAESID(id),
        FinalApproverComment: approverReviewerComment,
      }).unwrap();
      if (res?.hasError) {
        ToastService.error(res.errorMessage);
        return;
      }

      ToastService.success("Application has been approved successfully");
      addAuditLog({
        SGBPSApplicationId: editID,
        AuditLogDescription: "SGBS Application Approved by Final Approver",
        TriggredTimeStatusId: certificateGroupingDetailsData?.statusId ?? DEFAULT_STATUS_ID,
      });
        navigate("/viewApplicationTaskList");
    } catch (err) {
      ToastService.error(getApiErrorMessage(err));
    }
  };

  const handleApproverCommentChange = (e) => {
    setApproverReviewerComment(e.target.value);
  };

  const isPageLoading = productsAndServicesDropDownsLoading || productListLoading || certificateGroupingDetailsLoading || getPeerReviewEvaluationDetailsByIdLoading;
  const isActionLoading = returnToCaseOfficeLoading || updateStatusOnReturnLoading || approveApplicationLoading;

  return (
    <div className="certificate-grouping-list-page">
      <div className="crud-requirement-list">
        <div className="corporate-member-list">
          <div className="member-list">
            <div className="content-section">
              <ModalGroupingTable data={productList} />
            </div>
          </div>

          <div className="table-title mt-4">
            <h3>Certificate and Cost Allocation</h3>
          </div>
          <div className="member-list">
            <CostAllocation rows={costAllocationList} />
          </div>

          <div className="table-title mt-5 mb-4">
            <h3>Internal Notes</h3>
          </div>
          <div className="mt-2">
            <label className="form-label">Comments from Case Officer</label>
            <textarea className="form-control mt-1" rows="4" value={internalNotes} disabled></textarea>
          </div>

          <div className="mt-2">
            <label className="form-label">Peer Reviewer Comment</label>
            <textarea
              className="form-control mt-1"
              rows="4"
              value={peerReviewerComment}
              maxLength={200}
              disabled
            ></textarea>
          </div>

          <div className="mt-2">
            <label className="form-label">Approver Reviewer Comment</label>
            <textarea className="form-control mt-1" rows="4" value={approverReviewerComment} onChange={handleApproverCommentChange} ></textarea>
          </div>

          <div className="table-title mt-5 mb-4">
            <h3>Assign to</h3>
          </div>
          <div className="dropdown-select-wrapper">
            <div className="d-flex align-items-center mb-4">
              <label className="form-label mb-0 w-20">Peer Reviewer <span className="text-danger">*</span> </label>
              <div style={{ flex: 1 }}>
                <Select
                  className="custom-checkbox-select"
                  value={peerReviewer}
                  options={approverOptionsMemo}
                  placeholder="Select Peer Reviewer"
                  isDisabled={true}
                />
              </div>
            </div>
            <div className="d-flex align-items-center">
              <label className="form-label mb-0 w-20">Approver <span className="text-danger">*</span></label>
              <div style={{ flex: 1 }}>
                <Select
                  className="custom-checkbox-select"
                  value={approver}
                  options={approverOptionsMemo}
                  placeholder="Select Approver"
                  isDisabled={true}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-5">
            <Buttons buttonText="Back" buttonTypeClassName="black-btn" onClick={handleBack} />
            <div className="d-flex gap-3">
              <Buttons
                buttonText="Return"
                buttonTypeClassName="reject btn"
                onClick={handleReturn}
                isLoading={returnToCaseOfficeLoading || updateStatusOnReturnLoading}
                isDisable={isPageLoading || isActionLoading}
              />
              <Buttons
                buttonText="Approve"
                buttonTypeClassName="success-btn"
                onClick={handleApprove}
                isLoading={approveApplicationLoading}
                isDisable={isPageLoading || isActionLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateWithoutRatingFinalReview;
